const _jsxFileName = '';
import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles, Loader2 } from 'lucide-react';
import { sendConciergeMessage } from '../services/geminiService';

const Concierge = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: '1',
      text: 'Welcome to Sparkling Gems Concierge! How may I assist you with our luxury collection today?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!inputMessage.trim() || isLoading) return;

    // Add user message
    const userMessage = {
      id: Date.now().toString(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Get bot response
      const response = await sendConciergeMessage(inputMessage);

      const botMessage = {
        id: (Date.now() + 1).toString(),
        text: response,
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        text: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return React.createElement(
    React.Fragment,
    null,
    /* Chat Button */
    React.createElement(
      'button',
      {
        onClick: toggleChat,
        className:
          'fixed bottom-6 right-6 z-50 w-14 h-14 bg-gold-600 hover:bg-gold-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110',
        'aria-label': 'Open concierge chat',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 85 },
      },

      isOpen
        ? React.createElement(X, {
            size: 24,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 90 },
          })
        : React.createElement(MessageCircle, {
            size: 24,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 90 },
          })
    ),

    /* Chat Window */
    isOpen &&
      React.createElement(
        'div',
        {
          className:
            'fixed bottom-24 right-6 z-50 w-96 h-[500px] bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden border border-stone-200',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 95 },
        },
        /* Header */
        React.createElement(
          'div',
          {
            className: 'bg-gold-600 text-white p-4 flex items-center gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 97 },
          },
          React.createElement(Sparkles, {
            size: 20,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 98 },
          }),
          React.createElement(
            'h3',
            {
              className: 'font-serif text-lg',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 99 },
            },
            'Concierge'
          ),
          React.createElement(
            'span',
            {
              className: 'text-xs bg-white/20 px-2 py-1 rounded-full ml-auto',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 100 },
            },
            'Luxury Assistant'
          )
        ),

        /* Messages */
        React.createElement(
          'div',
          {
            className: 'flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 104 },
          },
          messages.map((message) =>
            React.createElement(
              'div',
              {
                key: message.id,
                className: `flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 106 },
              },

              React.createElement(
                'div',
                {
                  className: `max-w-[80%] p-3 rounded-lg ${
                    message.sender === 'user'
                      ? 'bg-gold-600 text-white rounded-br-none'
                      : 'bg-white text-stone-800 rounded-bl-none shadow-sm'
                  }`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 110 },
                },

                React.createElement(
                  'p',
                  {
                    className: 'text-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 117 },
                  },
                  message.text
                ),
                React.createElement(
                  'span',
                  {
                    className: 'text-[10px] opacity-70 mt-1 block',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 118 },
                  },
                  message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                )
              )
            )
          ),
          isLoading &&
            React.createElement(
              'div',
              {
                className: 'flex justify-start',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 125 },
              },
              React.createElement(
                'div',
                {
                  className: 'bg-white text-stone-800 p-3 rounded-lg rounded-bl-none shadow-sm',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 126 },
                },
                React.createElement(Loader2, {
                  size: 20,
                  className: 'animate-spin text-gold-600',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 127 },
                })
              )
            ),
          React.createElement('div', {
            ref: messagesEndRef,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 131 },
          })
        ),

        /* Input */
        React.createElement(
          'form',
          {
            onSubmit: handleSendMessage,
            className: 'p-4 border-t border-stone-200 bg-white',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 135 },
          },
          React.createElement(
            'div',
            {
              className: 'flex gap-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 136 },
            },
            React.createElement('input', {
              type: 'text',
              value: inputMessage,
              onChange: (e) => setInputMessage(e.target.value),
              placeholder: 'Type your message...',
              className:
                'flex-1 px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gold-600 text-sm',
              disabled: isLoading,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 137 },
            }),
            React.createElement(
              'button',
              {
                type: 'submit',
                disabled: isLoading || !inputMessage.trim(),
                className:
                  'p-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 145 },
              },

              React.createElement(Send, {
                size: 18,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 150 },
              })
            )
          )
        )
      )
  );
};

export default Concierge;
